<template>
  <div class='w-full h-full bg-white flex min-w-0 overflow-hidden'>
    <div class='w-13/24 min-w-0 flex-shrink-0 overflow-hidden px-8'>
      <settings-item-header :title="'Knowledge Bases'"
                            :description="'Manage your knowledge bases here: create new indexes, modify datasources, or get information.'"
                            :is-adding-new-item="isAddingNewIndex"
                            :is-button-hidden="isAddingNewIndexSource"
                            class="border-none"
                            @click="isAddingNewIndex ? cancelAddNewIndex() : addNewIndex()"
      />
      <with-loader id="settings-indexes-table-loader" :loading="loaders.indexes" class="h-full pb-10" use-as-overlay>
        <settings-indexes-list v-if="!isEmpty(indexes)"
                               :indexes="indexes"
                               :index-groups-by-id="indexGroupsById"
                               :selected-index-position="selectedIndexPosition"
                               :selected-index-source-position="selectedIndexSourcePosition"
                               :selected-panel-level="selectedPanelLevel"
                               :active-add-market-group-id="addMarketGroupId"
                               :is-index-name-loading="loaders.name"
                               :is-index-source-name-loading="loaders.indexSourceName"
                               :is-adding-new-index-source="isAddingNewIndexSource"
                               @rename="saveIndexName"
                               @rename-index-group="saveIndexGroupName"
                               @rename-index-source="renameIndexSource"
                               @update-index-source-position="updateIndexSourcePosition"
                               @delete-index-source="deleteIndexSource"
                               @request-delete="handleRequestDelete"
                               @request-delete-index-group="handleRequestDeleteIndexGroup"
                               @request-add-market="handleRequestAddMarket"
                               @show-add-index-source="setIsAddingNewIndexSource"
                               @start-reindex="startReindex"
                               @start-reindex-all-markets="startReindexAllMarkets"
                               @select-index-index="selectIndexIndex"
                               @select-market-index="selectMarketIndex"
                               class="h-5/6"
        />
      </with-loader>
    </div>

    <div class="w-11/24 min-w-0 flex-shrink-0 overflow-hidden">
      <transition name="slide-fade" mode="out-in">
        <settings-index-source-create v-if="isAddingNewIndexSource"
                                      :index="selectedIndex"
                                      :source-type.sync="sourceType"
                                      @display-index-editor="setIsAddingNewIndexSource({isAdding: false, sourceType: KNOWLEDGE_BASES.XML_FEED })"
                                      @post-process-index-source-create="postProcessIndexSourceCreate"
                                      class="w-full min-w-0 h-full overflow-hidden"
        />
        <settings-index-source-editor v-else-if="selectedIndexSource"
                                      :key="selectedIndexSource.id"
                                      :index="selectedIndex"
                                      :index-source="selectedIndexSource"
                                      :is-loading-name="loaders.indexSourceName"
                                      :is-loading-reindex="loaders.reindex"
                                      :is-saving="loaders.indexSource"
                                      :is-rename-mode="isRenameMode"
                                      class="w-full min-w-0 h-full overflow-hidden"
                                      @set-rename-mode="setRenameMode"
                                      @rename="renameIndexSource"
                                      @reload-indexes="reloadIndexes"
                                      @delete-index-source="deleteIndexSource"
                                      @start-reindex="startReindex"
                                      @display-index-editor="updateIndexSourcePosition(-1)"
        />
        <settings-indexes-add-market-editor v-else-if="isAddingMarket && selectedIndexGroup"
                                            :key="`add-market-${selectedIndexGroup.id}`"
                                            :group-name="selectedIndexGroup.name"
                                            :index-group-id="selectedIndexGroup.id"
                                            :indexes="indexes"
                                            @cancel="cancelAddMarket"
                                            @create="processNewMarketIndex"
                                            class="w-full min-w-0 h-full overflow-hidden"
        />
        <settings-index-editor v-else-if="selectedIndex"
                               :key="selectedIndex.id"
                               :index="selectedIndex"
                               :header-title="indexEditorHeaderTitle"
                               :is-index-group-panel="isIndexGroupPanel"
                               :group-markets="indexEditorGroupMarkets"
                               :is-loading-name="loaders.name"
                               :is-loading-reindex="loaders.reindex"
                               :is-rename-mode="isRenameMode"
                               class="w-full min-w-0 h-full overflow-hidden"
                               @set-rename-mode="setRenameMode"
                               @rename="handleIndexEditorRename"
                               @request-delete="handleIndexEditorDelete"
                               @reload-indexes="reloadIndexes"
                               @start-reindex="startReindex"
                               @start-reindex-all-markets="startReindexAllMarketsForSelectedGroup"
        />
        <settings-index-create v-else-if="isAddingNewIndex"
                               :indexes="indexes"
                               @process-new-index="processNewIndex"
                               class="w-full min-w-0 h-full overflow-hidden"
        />
      </transition>
    </div>

    <delete-dialog :is-shown="isDeleteDialogShown"
                   :item="indexToDelete"
                   :dialog-type="'prompt'"
                   :delete-title="deleteKind === 'indexGroup' ? 'Delete Knowledge Base Group?' : 'Delete Knowledge Base?'"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="'Delete'"
                   :input-placeholder="'Enter text'"
                   :required-prompt-value="requiredPromptValue"
                   @cancel="cancelDelete"
                   @delete="deleteIndex"
    >
      <template #delete-item-icon>
        <delete-index-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>
  </div>
</template>

<script>
import DeleteIndexIcon from '@/assets/images/icons/DeleteIndexIcon'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import SettingsItemHeader from '@/components/settings/SettingsItemHeader.vue'
import SettingsIndexesList from '@/components/settings/indexes/SettingsIndexesList.vue'
import SettingsIndexSourceEditor from '@/components/settings/indexes/SettingsIndexSourceEditor.vue'
import SettingsIndexEditor from '@/components/settings/indexes/SettingsIndexEditor.vue'
import SettingsIndexCreate from '@/components/settings/indexes/SettingsIndexCreate.vue'
import SettingsIndexSourceCreate from '@/components/settings/indexes/SettingsIndexSourceCreate.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import indexesService from '@/services/amio/indexes.service'
import indexGroupService from '@/services/amio/index-group.service'
import indexSourcesService from '@/services/amio/index-sources.service'
import indexExecutionsService from '@/services/amio/index-executions.service'
import SettingsIndexesAddMarketEditor from '@/components/settings/indexes/SettingsIndexesAddMarketEditor.vue'
import mixins from '@/utils/mixins'
import { buildIndexGroupsById, groupIndexes } from '@/utils/index.utils'
import {
  getIndexExecutionStatus,
  CREATED,
  DATA_PROCESSING,
  INDEX_PROCESSING,
  INDEX_HALTED,
  INDEX_UNKNOWN,
  RESTARTED
} from '@/utils/index-execution.utils'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import previewModule from '@/store/modules/preview.module'
import dissoc from 'ramda/src/dissoc'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import toaster from '@/utils/toaster'
import { ROUTE_NAMES } from '@/router'
import { ITEM_IDS } from '@/utils/editor.constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import marketModule from '@/store/modules/market.module'
import marketService from '@/services/amio/market.service'

const STATUS_POLL_INTERVAL_MS = 10000
const IN_PROGRESS_STATUSES = [CREATED, DATA_PROCESSING, INDEX_PROCESSING, INDEX_HALTED, INDEX_UNKNOWN, RESTARTED]

export default {
  name: 'SettingsIndexes',
  mixins: [mixins],
  components: {
    DeleteIndexIcon,
    DeleteDialog,
    SettingsItemHeader,
    SettingsIndexesList,
    SettingsIndexesAddMarketEditor,
    SettingsIndexSourceEditor,
    SettingsIndexEditor,
    SettingsIndexCreate,
    SettingsIndexSourceCreate,
    WithLoader
  },

  watch: {
    '$route.path': {
      immediate: false,
      handler(routePath) {
        if (routePath.includes(ITEM_IDS.NEW)) {
          this.selectedIndexPosition = -1
          this.addMarketGroupId = null
          this.selectedPanelLevel = 'group'
        } else {
          this.selectIndexByRoute()
        }
      }
    }
  },

  data() {
    return {
      selectedIndexPosition: -1,
      selectedIndexSourcePosition: -1,
      selectedPanelLevel: 'group',
      pendingPanelLevel: null,
      pendingIndexSourcePosition: -1,
      addMarketGroupId: null,
      indexGroupsById: {},
      isRenameMode: false,
      indexes: [],
      statusPollTimer: null,
      loaders: {
        indexes: false,
        indexSource: false,
        indexSourceName: false,
        name: false,
        reindex: false
      },
      indexToDelete: null,
      deleteKind: 'index',
      isDeleteDialogShown: false,
      isAddingNewIndexSource: false,
      isEmpty,
      sourceType: KNOWLEDGE_BASES.XML_FEED,
      KNOWLEDGE_BASES
    }
  },

  computed: {
    error: previewModule.error,

    markets: marketModule.getMarketList,

    selectedIndex() {
      return isEmpty(this.indexes) && this.selectedIndexPosition >= 0 ? null : this.indexes[this.selectedIndexPosition]
    },

    selectedIndexSource() {
      if (!this.selectedIndex || this.selectedIndexSourcePosition === -1) return null

      return this.selectedIndex.sources[this.selectedIndexSourcePosition]
    },

    deleteDescription() {
      if (!this.indexToDelete) return ''
      const itemLabel = this.deleteKind === 'indexGroup' ? 'knowledge base group' : 'knowledge base'
      const belongsToIndexGroup = this.deleteKind === 'indexGroup' || !!this.indexToDelete.index_group_id
      const groupWarning = belongsToIndexGroup ?
        (this.deleteKind === 'indexGroup' ?
          '<br/><br/>Bots using this knowledge base group will no longer be able to serve market-specific info to customers.' :
          '<br/><br/>Bots using this market will no longer be able to serve market-specific info to customers.') :
        ''
      return `This will delete all data for this ${itemLabel} "${this.indexToDelete.name}". It cannot be undone.${groupWarning}<br/><br/>Please type <strong>${this.indexToDelete.name}</strong> to enable delete.`
    },

    requiredPromptValue() {
      return this.indexToDelete ? this.indexToDelete.name : null
    },

    isAddingNewIndex() {
      return this.$route.path.includes(ITEM_IDS.NEW)
    },
    isAddingMarket() {
      return this.selectedPanelLevel === 'addMarket'
    },
    groupedIndexes() {
      return groupIndexes(this.indexes, this.indexGroupsById)
    },
    selectedIndexGroup() {
      if (!this.addMarketGroupId) return null
      return this.groupedIndexes.find(group => group.id === this.addMarketGroupId) || null
    },
    selectedGroupedIndex() {
      if (this.selectedIndexPosition < 0) return null

      const selectedIndex = this.indexes[this.selectedIndexPosition]
      if (!selectedIndex) return null

      return this.groupedIndexes.find(group => {
        if (group.primaryIndex?.id === selectedIndex.id) return true
        return group.markets.some(market => market.index?.id === selectedIndex.id)
      }) || null
    },
    isIndexGroupPanel() {
      return this.selectedPanelLevel === 'group' && this.selectedGroupedIndex?.hasIndexGroup === true
    },
    indexEditorHeaderTitle() {
      if (!this.isIndexGroupPanel) return null
      return this.selectedGroupedIndex?.name || null
    },
    indexEditorGroupMarkets() {
      if (!this.isIndexGroupPanel) return []
      return this.selectedGroupedIndex?.markets || []
    }
  },

  methods: {
    addNewIndex() {
      this.selectedIndexPosition = -1
      this.selectedPanelLevel = 'group'
      this.addMarketGroupId = null
      this.$router.push({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: ITEM_IDS.NEW } })
    },

    cancelAddNewIndex() {
      this.$router.push({ name: ROUTE_NAMES.knowledgeBases })
    },

    selectIndexIndex(indexPosition, indexSourcePosition = -1) {
      this.selectedIndexPosition = indexPosition
      this.selectedIndexSourcePosition = indexSourcePosition
      if (indexSourcePosition >= 0) {
        this.selectedPanelLevel = 'source'
        this.pendingPanelLevel = 'source'
        this.pendingIndexSourcePosition = indexSourcePosition
      } else {
        this.selectedPanelLevel = 'group'
        this.pendingPanelLevel = 'group'
      }
      this.addMarketGroupId = null
    },

    selectMarketIndex(indexPosition, indexSourcePosition = -1) {
      this.selectedIndexPosition = indexPosition
      this.selectedIndexSourcePosition = indexSourcePosition
      if (indexSourcePosition >= 0) {
        this.selectedPanelLevel = 'source'
        this.pendingPanelLevel = 'source'
        this.pendingIndexSourcePosition = indexSourcePosition
      } else {
        this.selectedPanelLevel = 'market'
        this.pendingPanelLevel = 'market'
      }
      this.addMarketGroupId = null
      this.setIsAddingNewIndexSource({ isAdding: false, sourceType: KNOWLEDGE_BASES.XML_FEED })
    },

    handleIndexEditorRename(updatedIndex) {
      if (this.isIndexGroupPanel && this.selectedGroupedIndex) {
        this.saveIndexGroupName({ id: this.selectedGroupedIndex.id, name: updatedIndex.name })
        return
      }
      this.saveIndexName(updatedIndex, this.selectedIndexPosition)
    },

    handleIndexEditorDelete(index) {
      if (this.isIndexGroupPanel && this.selectedGroupedIndex) {
        this.handleRequestDeleteIndexGroup(this.selectedGroupedIndex)
        return
      }
      this.handleRequestDelete(index)
    },

    handleRequestAddMarket(group) {
      if (this.addMarketGroupId === group.id) {
        this.cancelAddMarket()
        return
      }

      const indexPosition = this.indexes.findIndex(index => index.id === group.primaryIndex.id)
      if (indexPosition < 0) return

      // Source-create wins the right-panel v-if chain; close it so Add Market can show.
      this.setIsAddingNewIndexSource({ isAdding: false, sourceType: KNOWLEDGE_BASES.XML_FEED })
      this.addMarketGroupId = group.id
      this.selectedIndexPosition = indexPosition
      this.selectedIndexSourcePosition = -1
      this.selectedPanelLevel = 'addMarket'
      this.pendingPanelLevel = 'addMarket'
      this.$router.push({
        name: ROUTE_NAMES.knowledgeBases,
        params: { knowledgeBaseId: group.primaryIndex.id }
      })
    },

    cancelAddMarket() {
      this.addMarketGroupId = null
      if (this.selectedIndexPosition < 0) return
      this.selectIndexIndex(this.selectedIndexPosition)
    },

    async processNewMarketIndex(newIndex) {
      await this.reloadIndexes()
      const newIndexPosition = this.indexes.findIndex(index => index.id === newIndex.id)
      this.addMarketGroupId = null
      this.selectedIndexPosition = newIndexPosition >= 0 ? newIndexPosition : 0
      this.selectedIndexSourcePosition = -1
      this.selectedPanelLevel = 'market'
      this.pendingPanelLevel = 'market'
      this.$router.replace({
        name: ROUTE_NAMES.knowledgeBases,
        params: { knowledgeBaseId: newIndex.id }
      })
    },

    updateIndexPosition(indexPosition) {
      this.selectedIndexPosition = indexPosition
    },

    selectLastIndexSource() {
      this.updateIndexSourcePosition(this.selectedIndex.sources.length - 1)
    },

    updateIndexSourcePosition(indexSourcePosition) {
      this.selectedIndexSourcePosition = indexSourcePosition
      this.selectedPanelLevel = indexSourcePosition === -1 ? 'market' : 'source'
      if (indexSourcePosition >= 0) {
        this.pendingPanelLevel = 'source'
        this.pendingIndexSourcePosition = indexSourcePosition
      }
      this.setIsAddingNewIndexSource({ isAdding: false, sourceType: KNOWLEDGE_BASES.XML_FEED })
    },

    setIsAddingNewIndexSource({ isAdding, sourceType }) {
      this.isAddingNewIndexSource = isAdding
      this.sourceType = sourceType
      if (isAdding) {
        this.selectedIndexSourcePosition = -1
        this.selectedPanelLevel = 'addSource'
      }
    },

    selectIndexByRoute() {
      if (isEmpty(this.indexes)) return

      const keepAddMarketState = this.pendingPanelLevel === 'addMarket'
      if (!keepAddMarketState) this.addMarketGroupId = null

      const routeKnowledgeBaseId = this.$route.path.match(/\d+/g)?.[0] || null
      if (routeKnowledgeBaseId) {
        this.error = null
        const selectedIndexPosition = this.indexes.findIndex(knowledgeBase => knowledgeBase.id === routeKnowledgeBaseId)
        this.selectedIndexPosition = selectedIndexPosition
        if (this.pendingPanelLevel === 'source' && selectedIndexPosition >= 0) {
          this.selectedIndexSourcePosition = this.pendingIndexSourcePosition
          this.selectedPanelLevel = 'source'
        } else {
          this.selectedIndexSourcePosition = -1
          if (selectedIndexPosition >= 0) {
            const selectedIndex = this.indexes[selectedIndexPosition]
            this.selectedPanelLevel = this.pendingPanelLevel ||
              (selectedIndex.index_group_id ? 'market' : 'group')
          }
        }
        this.pendingPanelLevel = null
        this.pendingIndexSourcePosition = -1
        return
      }

      this.$router.replace({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: this.indexes[0].id } })
      this.selectedIndexPosition = 0
      this.selectedIndexSourcePosition = -1
      this.selectedPanelLevel = 'group'
      this.pendingPanelLevel = null
    },

    setRenameMode(isRenameMode) {
      this.isRenameMode = isRenameMode
    },

    handleRequestDelete(index) {
      this.indexToDelete = index
      this.deleteKind = 'index'
      this.isDeleteDialogShown = true
    },

    handleRequestDeleteIndexGroup(indexGroup) {
      this.indexToDelete = indexGroup
      this.deleteKind = 'indexGroup'
      this.isDeleteDialogShown = true
    },

    cancelDelete() {
      this.indexToDelete = null
      this.deleteKind = 'index'
      this.isDeleteDialogShown = false
    },

    async postProcessIndexSourceCreate() {
      await this.reloadIndexes()
      this.selectLastIndexSource()
    },

    async processNewIndex(newIndex) {
      await this.reloadIndexes()
      const indexPosition = this.indexes.findIndex(index => index.id === newIndex.id)
      this.selectedIndexPosition = indexPosition >= 0 ? indexPosition : this.indexes.length - 1
      this.selectedIndexSourcePosition = -1
      this.selectedPanelLevel = newIndex.index_group_id ? 'market' : 'group'
      this.$router.replace({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: newIndex.id } })
    },

    async renameIndexSource(updatedIndexSource) {
      try {
        this.loaders.indexSourceName = true
        const patchData = { ...dissoc('index_id', dissoc('id', updatedIndexSource)) }
        await indexSourcesService.update(this.selectedIndex.id, updatedIndexSource.id, patchData)
        await this.reloadIndexes()
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.indexSourceName = false
      }
    },

    async deleteIndexSource(indexSource) {
      try {
        await indexSourcesService.delete(this.selectedIndex.id, indexSource.id)
        await this.reloadIndexes()
        this.selectLastIndexSource()
      } catch (err) {
        toaster.error(err)
      }
    },

    async deleteIndex() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        if (this.deleteKind === 'indexGroup') {
          const groupIndexes = this.indexes.filter(index => index.index_group_id === this.indexToDelete.id)
          await Promise.all(groupIndexes.map(index => indexesService.delete(index.id)))
          await indexGroupService.delete(this.indexToDelete.id)
        } else {
          await indexesService.delete(this.indexToDelete.id)
        }
        await this.reloadIndexes()
        if (isEmpty(this.indexes)) {
          this.addNewIndex()
        } else {
          const selectedIndex = this.indexes[0]
          this.$router.replace({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: selectedIndex.id } })
          this.selectedIndexPosition = 0
        }
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.indexToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    async startReindex(index) {
      this.error = null
      if (index.id === this.selectedIndex.id) this.loaders.reindex = true
      try {
        toaster.info('Updating knowledge base... it may take a few minutes.')
        await indexExecutionsService.create(index.id)
        await this.reloadIndexes()
      } catch (err) {
        const data = err.response?.data
        const errorMessage = head(data?.details.errors)?.message || err.message
        this.error = errorMessage
        toaster.error(errorMessage)
      } finally {
        this.loaders.reindex = false
      }
    },

    async startReindexAllMarkets(indexGroup) {
      await this.reindexIndexesInGroup(indexGroup?.id)
    },

    async startReindexAllMarketsForSelectedGroup() {
      await this.reindexIndexesInGroup(this.selectedGroupedIndex?.id)
    },

    async reindexIndexesInGroup(indexGroupId) {
      if (!indexGroupId) return

      const groupIndexes = this.indexes.filter(index => index.index_group_id === indexGroupId)
      if (!groupIndexes.length) return

      this.error = null
      if (this.isIndexGroupPanel && this.selectedGroupedIndex?.id === indexGroupId) {
        this.loaders.reindex = true
      }

      try {
        toaster.info('Updating knowledge bases... it may take a few minutes.')
        await Promise.all(groupIndexes.map(index => indexExecutionsService.create(index.id)))
        await this.reloadIndexes()
      } catch (err) {
        const data = err.response?.data
        const errorMessage = head(data?.details.errors)?.message || err.message
        this.error = errorMessage
        toaster.error(errorMessage)
      } finally {
        this.loaders.reindex = false
      }
    },

    async saveIndexName({ id, name }, indexPosition) {
      try {
        this.loaders.name = true
        await indexesService.update(id, { name: name })
        await this.reloadIndexes()
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.name = false
      }
    },

    async saveIndexGroupName({ id, name }) {
      try {
        this.loaders.name = true
        await indexGroupService.update(id, { name })
        await this.reloadIndexes()
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.name = false
      }
    },

    async reloadIndexes({ showLoader = true } = {}) {
      if (showLoader) this.loaders.indexes = true
      const [indexes, indexGroups] = await Promise.all([
        indexesService.list(),
        indexGroupService.list()
      ])
      this.indexGroupsById = buildIndexGroupsById(indexGroups)
      this.indexes = indexes
      if (showLoader) this.loaders.indexes = false
      this.syncStatusPolling()
    },

    // Auto-refresh index status while any index is still processing, so the user doesn't
    // have to reload the page. Self-terminating: stops once everything has settled.
    syncStatusPolling() {
      const anyInProgress = this.indexes.some(index => IN_PROGRESS_STATUSES.includes(getIndexExecutionStatus(index)))
      if (anyInProgress) this.startStatusPolling()
      else this.stopStatusPolling()
    },

    startStatusPolling() {
      if (this.statusPollTimer) return
      this.statusPollTimer = setInterval(() => {
        this.reloadIndexes({ showLoader: false }).catch(() => {})
      }, STATUS_POLL_INTERVAL_MS)
    },

    stopStatusPolling() {
      if (!this.statusPollTimer) return
      clearInterval(this.statusPollTimer)
      this.statusPollTimer = null
    }
  },

  async mounted() {
    await this.reloadIndexes()
    const markets = await marketService.list()
    marketModule.setMarketList(markets)
    if (isEmpty(this.indexes)) {
      this.addNewIndex()
      return
    }

    if (!this.$route.path.includes(ITEM_IDS.NEW)) this.selectIndexByRoute()
  },

  beforeDestroy() {
    this.stopStatusPolling()
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
